import React, { useState } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../shared/Crud';
import { CopyIcon, CheckIcon } from '../Layout/icons';
import { getDeliveryStatusMeta } from './statusConfig';
import { normalizeDeliveryStateFromApi } from '../../utils/deliveryState';
import { maskCNPJ, maskCPF } from '../../utils/masks';

const { Button } = CrudStyles;

const COPIED_FEEDBACK_MS = 2000;

const CopyButton = styled(Button)`
    justify-content: center;
    white-space: nowrap;
`;

/** Empilha os dois rótulos na mesma célula: a largura fica igual à do texto mais longo. */
const LabelStack = styled.span`
    display: grid;
    justify-items: center;
`;

const Label = styled.span`
    grid-area: 1 / 1;
    visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            return document.execCommand('copy');
        } catch {
            return false;
        } finally {
            document.body.removeChild(textArea);
        }
    }
}

function formatAddressLine(addr) {
    if (!addr) return 'Não informado';
    const parts = [
        [addr.street, addr.number, addr.complement].filter(Boolean).join(', '),
        [addr.district, [addr.city, addr.stateAbbr].filter(Boolean).join('/')].filter(Boolean).join(' - '),
        addr.zipCode ? `CEP ${addr.zipCode}` : '',
    ].filter(Boolean);
    return parts.length ? parts.join(' - ') : 'Não informado';
}

/** Instruções do endereço; vazio quando nada foi informado. */
function formatInstructionsLines(label, instructions) {
    const text = String(instructions || '').trim();
    return text ? [`${label}: ${text}`] : [];
}

function formatItemLines(items) {
    if (!items?.length) return ['Nenhum item cadastrado'];
    return items.map((item) => {
        const name = String(item?.description || '').trim() || 'Item sem descrição';
        const quantity = String(item?.quantity ?? '').trim();
        return quantity ? `${quantity} - ${name}` : name;
    });
}

/** Linhas do cliente da entrega; vazio quando nome e documento não estão disponíveis. */
function formatCustomerLines(customerName, customerDocument) {
    const name = String(customerName || '').trim();
    const document = String(customerDocument || '').trim();
    const digits = document.replace(/\D/g, '');
    const lines = [];
    if (name) lines.push(`*Cliente:* ${name}`);
    if (document) {
        const isCpf = digits.length <= 11;
        lines.push(isCpf ? `*CPF:* ${maskCPF(digits)}` : `*CNPJ:* ${maskCNPJ(document)}`);
    }
    return lines.length ? [...lines, ''] : [];
}

/** Bloco do responsável pelo recebimento; vazio quando nenhum dado foi informado. */
function formatRecipientLines(recipientName, recipientDocument, recipientPhone) {
    const lines = [
        ['Nome', recipientName],
        ['CPF', recipientDocument],
        ['Telefone', recipientPhone],
    ]
        .filter(([, value]) => String(value || '').trim() !== '')
        .map(([label, value]) => `${label}: ${String(value).trim()}`);
    return lines.length ? ['*Responsável pelo recebimento:*', ...lines, ''] : [];
}

/** Mensagem em texto puro, com marcação em negrito compatível com o WhatsApp. */
export function buildDeliverySummaryText({
    deliveryId,
    deliveryState,
    customerName,
    customerDocument,
    pickupAddress,
    pickupInstructions,
    deliveryAddress,
    deliveryInstructions,
    recipientName,
    recipientDocument,
    recipientPhone,
    items,
}) {
    const statusLabel = getDeliveryStatusMeta(normalizeDeliveryStateFromApi(deliveryState)).label;
    return [
        `*Entrega nº ${deliveryId ?? '—'}*`,
        `*Status:* ${statusLabel}`,
        '',
        ...formatCustomerLines(customerName, customerDocument),
        '*Endereço de coleta:*',
        formatAddressLine(pickupAddress),
        ...formatInstructionsLines('Instruções de coleta', pickupInstructions),
        '',
        '*Endereço de entrega:*',
        formatAddressLine(deliveryAddress),
        ...formatInstructionsLines('Instruções de entrega', deliveryInstructions),
        '',
        ...formatRecipientLines(recipientName, recipientDocument, recipientPhone),
        '*Itens:*',
        ...formatItemLines(items),
    ].join('\n');
}

export default function CopyDeliverySummaryButton({
    deliveryId,
    deliveryState,
    customerName = '',
    customerDocument = '',
    pickupAddress,
    pickupInstructions = '',
    deliveryAddress,
    deliveryInstructions = '',
    recipientName = '',
    recipientDocument = '',
    recipientPhone = '',
    items,
    disabled = false,
}) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        const text = buildDeliverySummaryText({
            deliveryId,
            deliveryState,
            customerName,
            customerDocument,
            pickupAddress,
            pickupInstructions,
            deliveryAddress,
            deliveryInstructions,
            recipientName,
            recipientDocument,
            recipientPhone,
            items,
        });
        if (!(await copyToClipboard(text))) return;
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    };

    return (
        <CopyButton
            type="button"
            variant="outline"
            onClick={handleCopy}
            disabled={disabled}
            title="Copiar identificador, status, endereços e itens para compartilhar"
        >
            {copied ? <CheckIcon /> : <CopyIcon />}
            <LabelStack>
                <Label $visible={!copied}>Copiar dados da entrega</Label>
                <Label $visible={copied}>Dados copiados!</Label>
            </LabelStack>
        </CopyButton>
    );
}
